import React, { createContext, useContext, useState, useEffect, FC, ReactNode, useCallback } from 'react';
import { MaintenanceData, registerMaintenanceHandler } from 'src/utils/refetch';
import { createUrl } from 'src/core/api/utils/createUrl';
import { fetch as apiFetch } from 'src/utils/refetch';

type AppStatus = 'initializing' | 'ready' | 'maintenance';

interface AppGateState {
  status: AppStatus;
  maintenanceData: MaintenanceData | null;
}

const AppGateContext = createContext<AppGateState>({
  status: 'initializing',
  maintenanceData: null,
});

export const useAppGate = () => useContext(AppGateContext);

export const AppGateProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AppStatus>('initializing');
  const [maintenanceData, setMaintenanceData] = useState<MaintenanceData | null>(null);

  const enterMaintenance = useCallback((data: MaintenanceData) => {
    setMaintenanceData(data);
    setStatus('maintenance');
  }, []);

  // Register global 503 handler so ANY request that returns 503
  // triggers maintenance mode — not just the initial prefetch
  useEffect(() => {
    registerMaintenanceHandler(enterMaintenance);
  }, [enterMaintenance]);

  useEffect(() => {
    apiFetch(createUrl('/prefetch'))
      .then(() => {
        setStatus('ready');
      })
      .catch((error: any) => {
        if (error.response?.status === 503) {
          // Already handled by the global 503 handler above,
          // but we set it here too for the initial gate transition
          enterMaintenance(error.body ?? {});
        } else {
          setStatus('ready');
        }
      });
  }, [enterMaintenance]);

  return (
    <AppGateContext.Provider value={{ status, maintenanceData }}>
      {children}
    </AppGateContext.Provider>
  );
};
